import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Loader2, CheckCircle, Copy, Smartphone, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const UpiPaymentPage = ({ 
  isOpen, 
  onClose, 
  amount, 
  upiId, 
  payeeName, 
  transactionNote,
  onPaymentComplete,
  isEventCreation = false
}) => {
  const [transactionId, setTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getVerificationTime = () => {
    const currentHour = new Date().getHours();
    if (currentHour >= 8 && currentHour < 20) {
      return '2-4 hours (08 AM - 08 PM)';
    } else {
      return '8-10 hours (08 PM - 08 AM)';
    }
  };

  const handleCopyUpiId = () => {
    navigator.clipboard.writeText(upiId);
    toast.success('UPI ID copied to clipboard!');
  };

  const handleCopyAmount = () => {
    navigator.clipboard.writeText(amount.toString());
    toast.success('Amount copied to clipboard!');
  };

  const handlePayWithApp = (app) => {
    let deepLink = '';
    
    switch (app) {
      case 'gpay':
        deepLink = `tez://upi/pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
        break;
      case 'phonepe':
        deepLink = `phonepe://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
        break;
      case 'paytm':
        deepLink = `paytmmp://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
        break;
      case 'bhim':
        deepLink = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
        break;
      default:
        deepLink = upiString;
    }

    // Try to open the app
    window.location.href = deepLink;
    
    toast.info('Opening payment app...', {
      description: 'Complete the payment in your UPI app'
    });
  };

  const handleSubmitTransaction = () => {
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }

    setSubmitting(true);
    
    // Pass transaction ID to parent
    if (onPaymentComplete) {
      onPaymentComplete(transactionId);
    }
    
    toast.success('Transaction ID submitted! Verification in progress.');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Complete Your Payment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Amount Display */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Amount to Pay</p>
                <div className="flex items-center justify-center gap-3">
                  <p className="text-4xl font-bold text-green-600">
                    ₹{amount.toLocaleString()}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyAmount}
                    className="text-gray-500"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{transactionNote}</p>
              </div>
            </CardContent>
          </Card>

          {/* Payment Amount Display */}
          <Card className="bg-gradient-to-r from-contribo-navy to-contribo-navy-light border-none">
            <CardContent className="pt-6">
              <div className="text-center text-white space-y-2">
                <p className="text-sm opacity-90">Amount to Pay</p>
                <p className="text-5xl font-bold">₹{amount.toLocaleString()}</p>
                <p className="text-sm opacity-90">to {payeeName}</p>
              </div>
            </CardContent>
          </Card>

          {/* UPI ID Display */}
          <Card className="bg-blue-50 border-2 border-blue-200">
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">Pay to UPI ID:</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyUpiId}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="font-mono text-lg font-bold text-contribo-navy break-all bg-white p-3 rounded border">
                  {upiId}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-800 font-semibold mb-2">📝 Payment Instructions:</p>
            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
              <li>Copy the UPI ID shown above</li>
              <li>Open your UPI app (GPay, PhonePe, Paytm, etc.)</li>
              <li>Send ₹{amount.toLocaleString()} to the UPI ID</li>
              <li>Save your UPI transaction ID from payment confirmation</li>
              <li>Enter the transaction ID below and submit</li>
            </ol>
          </div>

          {/* Transaction ID Input */}
          <div className="space-y-2">
            <Label htmlFor="transaction-id" className="text-contribo-navy font-semibold">
              UPI Transaction ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="transaction-id"
              placeholder="e.g., 123456789012"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="text-lg"
            />
            <p className="text-xs text-gray-500">
              Enter the 12-digit transaction ID from your UPI payment confirmation
            </p>
          </div>

          {/* Verification Time Notice */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-yellow-800 mb-2">⏱️ Verification Time</p>
            {isEventCreation && (
              <p className="text-sm font-bold text-yellow-900 mb-2">
                Current verification time: {getVerificationTime()}
              </p>
            )}
            <p className="text-xs text-yellow-700">
              • <strong>08 AM - 08 PM:</strong> Verification in 2-4 hours<br/>
              • <strong>08 PM - 08 AM:</strong> Verification in 8-10 hours
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              Your {isEventCreation ? 'event will be created immediately' : 'RSVP will be recorded'}, but payment verification is pending.
            </p>
          </div>

          {/* Submit Button */}
          <Button
            className="w-full bg-contribo-navy hover:bg-contribo-navy-light text-white py-6 text-lg"
            onClick={handleSubmitTransaction}
            disabled={!transactionId.trim() || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Submit Transaction ID
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500">
            Admin will verify your payment and approve within the stated time
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpiPaymentPage;
